import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  PRESCRIPTION_ISSUED_AWAITING_SUBSCRIPTION_LABEL,
  type AwaitingActivationRow,
} from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import { UnauthorizedError } from "@/lib/clinician-api";
import { awaitingActivationViewRow } from "@/lib/awaiting-activation";
import { fetchAwaitingActivation } from "@/lib/awaiting-activation-api";
import { adminPatientHref } from "@/lib/patient-links";

// TES-1150 (Emelie 2026-10-07) — the operational list
// "Recept utfärdat – inväntar abonnemangsaktivering": the doctor's part is
// done, the subscription is not verified. For administrative follow-up and
// reminders; "Dessa patienter ska inte framställas som medicinskt ofärdiga."
//
// Admin only, like Kontaktlista: the list names people, so the API logs it
// as a read of each patient it returns. "Patienten" opens her through the
// admin's support view, which asks for today's support reason there.
export function AwaitingActivationRoute() {
  const { signOut, claims } = useAuth();
  const signOutRef = useRef(signOut);
  signOutRef.current = signOut;
  const [rows, setRows] = useState<AwaitingActivationRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setRows((await fetchAwaitingActivation()).rows);
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        void signOutRef.current();
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="flex flex-col gap-md">
      <div className="flex flex-col gap-xs">
        <h1 className="font-display text-3xl tracking-tight">
          {PRESCRIPTION_ISSUED_AWAITING_SUBSCRIPTION_LABEL}
        </h1>
        <p className="text-sm text-textSecondary max-w-2xl">
          Läkaren har slutfört journal, behandlingsbeslut och recept. Patienten
          ser receptet först när abonnemanget är verifierat aktiverat. Listan är
          till för administrativ uppföljning och påminnelser. Internt: listan
          loggas som åtkomst till varje person den visar.
        </p>
      </div>
      {error ? (
        <div className="bg-danger/10 border border-danger/30 text-danger rounded-md p-md text-sm">
          {error}
        </div>
      ) : null}
      {rows === null && !error ? (
        <p className="text-sm text-textSecondary">Hämtar…</p>
      ) : null}
      {rows ? (
        <AwaitingActivationView rows={rows} actingRole={claims?.role} />
      ) : null}
    </div>
  );
}

/** The table itself, or the empty state. Exported for the tests. */
export function AwaitingActivationView({
  rows,
  actingRole,
}: {
  rows: AwaitingActivationRow[];
  actingRole: string | undefined;
}) {
  if (rows.length === 0) {
    return (
      <div className="bg-surface border border-surfaceMuted rounded-md p-md text-textSecondary text-sm">
        Ingen patient väntar på abonnemangsaktivering just nu.
      </div>
    );
  }
  return (
    <div className="border border-surfaceMuted rounded-md bg-surface overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs uppercase tracking-wide text-textSecondary text-left">
            <th className="px-md py-sm font-medium">Patient</th>
            <th className="px-md py-sm font-medium">Patienttyp</th>
            <th className="px-md py-sm font-medium">Behandlingsbeslut</th>
            <th className="px-md py-sm font-medium">Recept utfärdat</th>
            <th className="px-md py-sm font-medium">Abonnemangsnivå</th>
            <th className="px-md py-sm font-medium">Aktiveringsstatus</th>
            <th className="px-md py-sm font-medium">Senaste påminnelse</th>
            <th className="px-md py-sm font-medium" />
          </tr>
        </thead>
        <tbody>
          {rows.map(awaitingActivationViewRow).map((r) => (
            <tr
              key={r.patientId}
              className="border-t border-surfaceMuted align-top"
            >
              <td className="px-md py-sm font-medium">{r.name}</td>
              <td className="px-md py-sm">{r.group}</td>
              <td className="px-md py-sm">{r.decided}</td>
              <td className="px-md py-sm">
                {r.prescribed}
                <span className="block text-xs text-textSecondary">
                  {r.prescription}
                </span>
              </td>
              <td className="px-md py-sm">{r.plan}</td>
              <td className="px-md py-sm">{r.activation}</td>
              <td className="px-md py-sm">{r.lastReminder}</td>
              <td className="px-md py-sm">
                <Link
                  to={adminPatientHref(actingRole, r.patientId)}
                  className="text-brand hover:underline"
                >
                  Patienten
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
